import React, { ReactChild, ReactElement } from 'react';
import { Formik, FormikValues, FormikProps, FormikHelpers } from 'formik';
import { Tag, Button, Spin, App } from 'antd';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { shallowEqual, useSelector } from 'react-redux';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import saveTranslation from '../../../constants/SaveTranslation.json';
import Page from './Page';
import FormErrorFocus from '../Forms/FormErrorFocus';
import PageFormDirtyHandler from './PageFormDirtyHandler';
import { FormSaveShortcut } from '../Forms/FormSaveShortcut';
import { updatePageFormDirtyState } from '../../../actions/catalogue/catalogue/update';
import { AsyncDispatch } from '../../../../types/global';
import { ApplicationState } from '../../../reducers';
import { useBlockerConfirm } from '../../../hooks/useBlockerConfirm';
import { useAppDispatch } from '../../../store/ReduxStore';
import { hasBrandEditPermission } from '../../../selectors/userSelector';

type PageFormikProps = {
  initialValues: FormikValues;
  validationSchema?: any;
  onSubmit: (values: FormikValues, formikActions: CustomFormikActions) => void;
  onCancel?: () => void;
  children: (formikBag: FormikProps<FormikValues>) => ReactChild | null;
  renderHeader?: (formikBag: FormikProps<FormikValues>) => ReactElement;
  handleSaveButtonEnabled?: (formik: FormikProps<FormikValues>) => boolean;
  showAnalysis?: boolean;
  fetchingData?: boolean;
  contentNoSpacing?: boolean;
  contentNoScroll?: boolean;
  enableReinitialize?: boolean;
  showExpandButton?: boolean;
  showNoError?: boolean;
  validateOnChange?: boolean;
};

export type CustomFormikActions = FormikHelpers<FormikValues> & {
  setSubmitPending: () => void;
  setSubmitSuccess: () => void;
  setSubmitError: (message?: string) => void;
};

enum FormSubmissionStatus {
  'EDIT' = 'EDIT',
  'SUCCESS' = 'SUCCESS',
  'ERROR' = 'ERROR',
}

const PageFormik: React.FC<PageFormikProps> = props => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { modal } = App.useApp();

  const [nextRouteLocation, setNextRouteLocation] = React.useState<any>(null);
  const [navigateModalVisible, setNavigateModalVisible] = React.useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | undefined>(undefined);

  const { brandId, pageFormDirty, brandEditPermission } = useSelector(
    (state: ApplicationState) => ({
      brandId: state.parent.brands.selectedBrandId,
      pageFormDirty: state.catalogue.catalogue.pageFormDirty,
      brandEditPermission: hasBrandEditPermission(state),
    }),
    shallowEqual
  );

  const { blockerActive, next, onConfirm, onCancel } = useBlockerConfirm(
    pageFormDirty && !nextRouteLocation
  );

  React.useEffect(() => {
    if (nextRouteLocation) navigate(nextRouteLocation);
  }, [navigate, nextRouteLocation]);

  React.useEffect(() => {
    return () => {
      dispatch(updatePageFormDirtyState(false));
      if (blockerActive) onCancel();
    };
  }, []);

  React.useEffect(() => {
    const showModal = (next: string, onConfirm: () => void, onCancel: () => void) => {
      if (next === nextRouteLocation) {
        setNextRouteLocation('');
        onConfirm();
        return;
      }
      setNavigateModalVisible(true);
      modal.confirm({
        title: saveTranslation.closeWarningTitle,
        icon: <ExclamationCircleOutlined />,
        onOk() {
          setNextRouteLocation(next);
          setNavigateModalVisible(false);
          onConfirm();
        },
        onCancel() {
          setNextRouteLocation('');
          setNavigateModalVisible(false);
          onCancel();
        },
      });
    };
    if (next && !navigateModalVisible) showModal(next, onConfirm, onCancel);
  }, [modal, navigateModalVisible, next, nextRouteLocation, onCancel, onConfirm]);

  return props.fetchingData ? (
    <div className="flex justify-center items-center flex-1">
      <Spin size="large" />
    </div>
  ) : (
    <Formik
      enableReinitialize={props.enableReinitialize}
      initialStatus={FormSubmissionStatus.EDIT}
      initialValues={props.initialValues}
      validationSchema={props.validationSchema}
      onSubmit={(values, formikActions) => {
        const actions = {
          ...formikActions,
          setSubmitPending: () => {
            formikActions.setSubmitting(true);
            setSubmitErrorMessage(undefined);
          },
          setSubmitSuccess: () => {
            formikActions.resetForm({ values, status: FormSubmissionStatus.SUCCESS });
            setTimeout(() => formikActions.setStatus(FormSubmissionStatus.EDIT), 2500);
          },
          setSubmitError: (message?: string) => {
            if (props.showNoError) {
              formikActions.setSubmitting(false);
              formikActions.setStatus(FormSubmissionStatus.EDIT);
            } else {
              formikActions.setSubmitting(false);
              formikActions.setStatus(FormSubmissionStatus.ERROR);
              setSubmitErrorMessage(message);
            }
          },
        };
        props.onSubmit(values, actions);
      }}
      validateOnChange={props.validateOnChange}
    >
      {formikBag => {
        const saveButtonEnabled = props.handleSaveButtonEnabled
          ? props.handleSaveButtonEnabled(formikBag)
          : formikBag.dirty && (!brandId || (brandId && brandEditPermission));
        const show =
          (formikBag.dirty && FormSubmissionStatus.EDIT === formikBag.status) ||
          FormSubmissionStatus.SUCCESS === formikBag.status ||
          FormSubmissionStatus.ERROR === formikBag.status;
        return (
          <Page
            showExpandButton={props.showExpandButton}
            showAnalysis={props.showAnalysis}
            contentNoSpacing={props.contentNoSpacing}
            contentNoScroll={props.contentNoScroll}
            renderHeader={props.renderHeader && props.renderHeader(formikBag)}
            renderBottom={
              show ? (
                <React.Fragment>
                  <div
                    className={classNames('status-bar flex items-center', {
                      success: formikBag.status === FormSubmissionStatus.SUCCESS,
                      error: formikBag.status === FormSubmissionStatus.ERROR,
                    })}
                  >
                    {formikBag.status === FormSubmissionStatus.ERROR && !formikBag.isSubmitting && (
                      <Tag color="red">{submitErrorMessage || saveTranslation.errorSaving}</Tag>
                    )}
                  </div>
                  <div className="page__bottom-save flex items-center">
                    <div className="flex items-center flex-1">
                      {!formikBag.isValid && formikBag.submitCount > 0 && (
                        <Tag color="red">{saveTranslation.fieldsMissing}</Tag>
                      )}
                      <div className="flex-1 flex justify-end">
                        <Button
                          className="page__bottom-save-cancel"
                          onClick={() => {
                            if (props.onCancel) {
                              props.onCancel();
                            }
                            formikBag.resetForm(formikBag.initialValues);
                            formikBag.setStatus(FormSubmissionStatus.EDIT);
                          }}
                        >
                          {saveTranslation.cancel}
                        </Button>

                        <Button
                          data-testid="form-save"
                          type="primary"
                          onClick={() => formikBag.handleSubmit()}
                          disabled={!saveButtonEnabled}
                          loading={formikBag.isSubmitting}
                        >
                          {saveTranslation.save}
                        </Button>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ) : null
            }
          >
            <PageFormDirtyHandler />
            <FormSaveShortcut onSave={formikBag.handleSubmit} enabled={saveButtonEnabled} />

            {props.children(formikBag)}

            <FormErrorFocus />
          </Page>
        );
      }}
    </Formik>
  );
};

export default PageFormik;
